import { singleton } from 'tsyringe';
import { faker } from '@faker-js/faker/locale/de';
import { Interval, DateTime } from 'luxon';
import { Employee } from '../domain/entities/employee.entity';
import { ShortIdHelper } from '../helpers/short-id.helper';

/**
 * Fake issue data type for timeline demo purposes.
 * Uses plain objects instead of full Entity instances to avoid EntityManager dependencies.
 */
export interface FakeIssue {
    uid: string;
    title: string;
    employees: Employee[];
    interval: Interval;
}

/**
 * Service for generating fake issue data for timeline demo purposes.
 * Creates plain objects that can be used without EntityManager.
 */
@singleton()
export class FakeIssueDataService {
    /**
     * Generates fake issues with random employee assignments.
     * @param count Number of issues to generate
     * @param employees Available employees for random assignment
     * @returns Plain object issues (no EntityManager)
     */
    public generateIssues(count: number, employees: Employee[]): FakeIssue[] {
        const issues: FakeIssue[] = [];
        const now = DateTime.now().startOf('day');

        for (let i = 0; i < count; i++) {
            // Random start date: -30 to +90 days from today
            const startOffset = faker.number.int({ min: -30, max: 90 });
            // Random duration: 1-14 days
            const duration = faker.number.int({ min: 1, max: 14 });
            const start = now.plus({ days: startOffset });
            const end = start.plus({ days: duration });

            // Random number of assignees: 1-3
            const assigneeCount = faker.number.int({ min: 1, max: Math.min(3, employees.length) });
            const assignedEmployees = faker.helpers.arrayElements(employees, assigneeCount);

            issues.push({
                uid: ShortIdHelper.generate(),
                title: faker.company.catchPhrase(),
                employees: assignedEmployees,
                interval: Interval.fromDateTimes(start, end)
            });
        }

        return issues;
    }
}
